import { Annotation } from "@/types/annotations";
import { Line, Circle } from "react-konva";
import { AnnotationType, Point } from "../types";
import { AnnotationViewerBase } from "../AnnotationViewerBase";
import { AnnotationSidebar } from "../AnnotationSidebar";
import {
  applyConfidenceThresholdToKeypointAnnotations,
  findNearestKeypointId,
  getKeyPointColor,
  inverseTransformPoint,
  isInsideMeasureBoundary,
  KeypointCandidate,
  transformPoint,
} from "./helpers";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ParsedKeypointAnnotation } from "./types";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { countPerSquareMeterToPerAcre, formatCountPerAcre, COUNT_PER_ACRE_TOOLTIP } from "@/lib/standCountUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { generateManualAnnotationId } from "../manualAnnotationIds";
import {
  demoteOutsideBoundaryKeypoints,
  getOutsideBoundaryCandidateState,
  isAnnotationEligibleForOutsideBoundaryReveal,
  OUTSIDE_BOUNDARY_CANDIDATE_COLOR,
  OUTSIDE_BOUNDARY_CANDIDATE_OUTLINE_COLOR,
  promoteOutsideBoundaryKeypoints,
} from "../outsideBoundaryAnnotations";
import type { OutsideBoundaryKeypointRestoreState } from "../outsideBoundaryAnnotations";
import { OutsideBoundaryWarningDialog } from "../OutsideBoundaryWarningDialog";
import { getOutsideBoundarySessionChoice, setOutsideBoundarySessionChoice } from "../outsideBoundaryWarningSession";
import { DEFAULT_ANNOTATION_VIEW_SETTINGS } from "../annotationViewSettings";

const KEYPOINT_RADIUS = 20;
// Forgiving targets in fit-view image pixels; inverse-zoomed below to keep their screen footprint.
const KEYPOINT_HIT_MULTIPLIER = 1.5;
const MIN_KEYPOINT_HIT_RADIUS_PX = 12;
const EMPTY_BOUNDARY_PIXELS: Point[] = [];

type KeyPointsProps = {
  imageUrl: string;
  /** Stable capture identity used to reset reversible, image-local edit state. */
  editorContextKey?: string;
  annotations: {
    list: Annotation[];
    measureBoundaryPixels?: Point[];
    annotationType: AnnotationType;
  };
  displayAnnotations?: boolean;
  isEditMode?: boolean;
  parsedKeypointAnnotations: ParsedKeypointAnnotation[];
  onKeypointAnnotationsChange: (annotations: ParsedKeypointAnnotation[]) => void;
  boundaryAreaSquareMeters?: number;
  orientation: string;
  rawWidth: number;
  rawHeight: number;
  /** Persisted stand-count sensitivity to restore on mount (0 = no restore). */
  initialConfidenceThreshold?: number | null;
  /** Reports the current sensitivity upward so the session snapshot can persist it. */
  onConfidenceThresholdChange?: (threshold: number) => void;
  /** Session-only dot size; does not change annotations or exported images. */
  initialPointSizePercent?: number;
  onPointSizeChange?: (percent: number) => void;
  /** Edit-only control for accepting boundary-hidden detections into the working annotation set. */
  showOutsideBoundaryAnnotations?: boolean;
  onShowOutsideBoundaryAnnotationsChange?: (value: boolean) => void;
};

export const KeyPoints = ({
  imageUrl,
  editorContextKey = imageUrl,
  annotations,
  displayAnnotations,
  isEditMode,
  parsedKeypointAnnotations,
  onKeypointAnnotationsChange,
  boundaryAreaSquareMeters,
  orientation,
  rawWidth,
  rawHeight,
  initialConfidenceThreshold,
  onConfidenceThresholdChange,
  initialPointSizePercent,
  onPointSizeChange,
  showOutsideBoundaryAnnotations = false,
  onShowOutsideBoundaryAnnotationsChange,
}: KeyPointsProps) => {
  const [confidenceThreshold, setConfidenceThreshold] = useState(initialConfidenceThreshold ?? 0);
  const [pointSizePercent, setPointSizePercent] = useState(
    initialPointSizePercent ?? DEFAULT_ANNOTATION_VIEW_SETTINGS.keypointSizePercent
  );
  const [totalScale, setTotalScale] = useState(1);
  const [hoveredKeypointId, setHoveredKeypointId] = useState<string | null>(null);
  const outsideBoundaryStateBeforePromotionRef = useRef(new Map<string, OutsideBoundaryKeypointRestoreState>());

  useEffect(() => {
    outsideBoundaryStateBeforePromotionRef.current.clear();
  }, [editorContextKey]);

  const [pendingOutsidePoint, setPendingOutsidePoint] = useState<{ x: number; y: number } | null>(null);
  const renderedPendingOutsidePoint = useRetainedValue(pendingOutsidePoint);

  const hasConfidenceData = useMemo(() => {
    const values = parsedKeypointAnnotations.map(({ confidence }) => confidence).filter((c) => c !== undefined);
    return values.length > 0 && new Set(values).size > 1;
  }, [parsedKeypointAnnotations]);

  const hasValidBoundary = (annotations.measureBoundaryPixels?.length ?? 0) >= 3;
  const passesConfidence = useCallback(
    ({ confidence, userVisibility }: ParsedKeypointAnnotation) =>
      userVisibility.manualOverride === true || confidence === undefined || confidence >= confidenceThreshold,
    [confidenceThreshold]
  );

  const countedAnnotations = useMemo(
    () =>
      parsedKeypointAnnotations.filter(
        (annotation) =>
          annotation.keypoint.visibility !== 0 &&
          annotation.userVisibility.isVisible !== false &&
          passesConfidence(annotation)
      ),
    [parsedKeypointAnnotations, passesConfidence]
  );

  const visibleAnnotations = useMemo(
    () =>
      parsedKeypointAnnotations.filter((annotation) => {
        if (!passesConfidence(annotation)) return false;
        const outsideBoundaryState = getOutsideBoundaryCandidateState(annotation);
        if (outsideBoundaryState) {
          return Boolean(isEditMode && displayAnnotations && hasValidBoundary && showOutsideBoundaryAnnotations);
        }
        // A counted annotation has already been accepted by capture or by a reviewer.
        // The reveal toggle only gates boundary-hidden candidates; recomputed geometry
        // must never make an accepted outside point disappear or become uneditable.
        return annotation.keypoint.visibility !== 0 && annotation.userVisibility.isVisible !== false;
      }),
    [
      displayAnnotations,
      hasValidBoundary,
      isEditMode,
      parsedKeypointAnnotations,
      passesConfidence,
      showOutsideBoundaryAnnotations,
    ]
  );

  // Captures store the boundary in display coordinates (see getSegmentationDisplayBoundaryPixels).
  // Unlike the keypoints below, which legacy AR captures keep in sensor space, it is never rotated.
  const displayBoundaryPixels = annotations.measureBoundaryPixels ?? EMPTY_BOUNDARY_PIXELS;

  // Precompute visible keypoint centers in display coords so double-click / hover
  // hit-testing is a tight, allocation-free scan.
  const keypointCandidates = useMemo<KeypointCandidate[]>(() => {
    const visibleIds = new Set(visibleAnnotations.map(({ id }) => id));
    return parsedKeypointAnnotations
      .filter(({ id }) => visibleIds.has(id))
      .map(({ id, keypoint }) => {
        const display = transformPoint(keypoint.x, keypoint.y, rawWidth, rawHeight, orientation);
        return { id, displayX: display.x, displayY: display.y };
      });
  }, [orientation, parsedKeypointAnnotations, rawHeight, rawWidth, visibleAnnotations]);

  // Scale the dot, outline and dash pattern together without changing the zoom policy.
  const pointLayerSize = (px: number) => (px * pointSizePercent) / (100 * totalScale);

  // Keep small dots easy to hit, but inverse-zoom the floor too: a fixed image-pixel
  // floor would swallow nearby additions at high zoom. initialScale cancels because
  // clicks and these radii are both in pre-initialScale image coordinates.
  const effectiveRadius = Math.max(
    pointLayerSize(KEYPOINT_RADIUS) * KEYPOINT_HIT_MULTIPLIER,
    MIN_KEYPOINT_HIT_RADIUS_PX / totalScale
  );

  const addKeypoint = useCallback(
    (point: { x: number; y: number }) => {
      const raw = inverseTransformPoint(point.x, point.y, rawWidth, rawHeight, orientation);
      onKeypointAnnotationsChange([
        ...parsedKeypointAnnotations,
        {
          id: generateManualAnnotationId(),
          keypoint: { x: raw.x, y: raw.y, visibility: 2 },
          source: "user",
          color: getKeyPointColor("user"),
          userVisibility: { isVisible: true, manualOverride: true },
        },
      ]);
    },
    [parsedKeypointAnnotations, onKeypointAnnotationsChange, orientation, rawWidth, rawHeight]
  );

  const onDblClickKeyPoint = useCallback(
    (id: string) => {
      const annotation = parsedKeypointAnnotations.find((a) => a.id === id);
      if (!annotation) return;
      if (annotation.source === "user") {
        onKeypointAnnotationsChange(parsedKeypointAnnotations.filter((a) => a.id !== id));
      } else {
        onKeypointAnnotationsChange(
          parsedKeypointAnnotations.map((a) => {
            if (a.id !== id) return a;

            const existingReasons = a.userVisibility.hiddenReasons ?? [];
            if (existingReasons.includes("userToggledOff")) {
              const restoredReasons = existingReasons.filter((reason) => reason !== "userToggledOff");
              const restoredVisibility =
                restoredReasons.length === 0
                  ? { isVisible: true, manualOverride: true }
                  : {
                      isVisible: false,
                      manualOverride: true,
                      hiddenReasons: restoredReasons,
                    };
              return {
                ...a,
                keypoint: {
                  ...a.keypoint,
                  visibility:
                    restoredReasons.length === 0 ? (a.keypoint.visibility === 1 ? 1 : 2) : a.keypoint.visibility,
                },
                userVisibility: restoredVisibility,
                hiddenReason: restoredReasons.length === 1 ? restoredReasons[0] : undefined,
                visibleVisibility:
                  restoredReasons.length > 0 && a.keypoint.visibility !== 0 ? a.keypoint.visibility : undefined,
              };
            }

            const hiddenReasons = [...existingReasons, "userToggledOff" as const];
            return {
              ...a,
              keypoint: {
                ...a.keypoint,
                // Boundary-hidden iOS keypoints can retain COCO visibility 1 or 2
                // while userVisibility performs the actual hide. Rejecting one in
                // the web editor must not destroy that independent COCO value.
                visibility: existingReasons.includes("outsideBoundary")
                  ? a.keypoint.visibility
                  : a.keypoint.visibility === 1
                    ? 1
                    : 0,
              },
              userVisibility: {
                isVisible: false,
                manualOverride: true,
                hiddenReasons,
              },
              hiddenReason: hiddenReasons.length === 1 ? hiddenReasons[0] : undefined,
              visibleVisibility: a.keypoint.visibility !== 0 ? a.keypoint.visibility : a.visibleVisibility,
            };
          })
        );
      }
    },
    [parsedKeypointAnnotations, onKeypointAnnotationsChange]
  );

  const onDoubleClick = useCallback(
    (point: { x: number; y: number }) => {
      if (!isEditMode || !displayAnnotations) return;

      // If the click is near an existing keypoint, toggle/remove that one (closest to center).
      const hitId = findNearestKeypointId(point, keypointCandidates, effectiveRadius);
      if (hitId) {
        onDblClickKeyPoint(hitId);
        return;
      }

      // A present but malformed boundary is corrupt metadata, not permission to
      // treat the image as full-frame or offer the normal outside-area override.
      if (displayBoundaryPixels.length > 0 && displayBoundaryPixels.length < 3) return;

      if (isInsideMeasureBoundary(displayBoundaryPixels, point)) {
        addKeypoint(point);
        return;
      }

      // Outside boundary
      const outsideBoundaryChoice = getOutsideBoundarySessionChoice("keypoint");
      if (outsideBoundaryChoice === "allow") {
        addKeypoint(point);
        return;
      }

      // First outside-boundary click — show warning dialog
      setPendingOutsidePoint(point);
    },
    [
      isEditMode,
      displayAnnotations,
      keypointCandidates,
      effectiveRadius,
      onDblClickKeyPoint,
      displayBoundaryPixels,
      addKeypoint,
    ]
  );

  const handleImageMouseMove = useCallback(
    (point: { x: number; y: number }) => {
      if (!isEditMode || !displayAnnotations) {
        setHoveredKeypointId(null);
        return;
      }
      // Skip the state update when the nearest keypoint is unchanged to avoid re-render churn.
      const nextId = findNearestKeypointId(point, keypointCandidates, effectiveRadius);
      setHoveredKeypointId((prev) => (prev === nextId ? prev : nextId));
    },
    [isEditMode, displayAnnotations, keypointCandidates, effectiveRadius]
  );

  const handleConfidenceThresholdChange = useCallback(
    (threshold: number) => {
      setConfidenceThreshold(threshold);
      const { changed, annotations } = applyConfidenceThresholdToKeypointAnnotations(
        parsedKeypointAnnotations,
        threshold
      );
      if (changed) onKeypointAnnotationsChange(annotations);
      onConfidenceThresholdChange?.(threshold);
    },
    [parsedKeypointAnnotations, onKeypointAnnotationsChange, onConfidenceThresholdChange]
  );

  // One-shot restore: commit the persisted sensitivity's keypoint hides the first
  // time annotations are available (they arrive via props and may be empty on
  // mount). This runs ONLY in edit mode — its purpose is to mark the image dirty
  // (updateKeypointAnnotations only flags dirty in edit mode) so Save persists the
  // inherited threshold; firing it in preview would burn the one-shot guard without
  // flagging dirty, leaving a later Edit → Save reporting no changes (GLE round-4 P1),
  // and preview stays view-only (visibleAnnotations already filters the render by the
  // restored threshold without mutating data). Gating on isEditMode makes the effect
  // re-run and fire on the preview→edit transition. The ref guard keeps this from
  // re-firing as the list later changes, and a no-op threshold never touches the data.
  const restoreAppliedRef = useRef(false);
  useEffect(() => {
    if (restoreAppliedRef.current) return;
    if (!isEditMode) return;
    const threshold = initialConfidenceThreshold ?? 0;
    if (threshold <= 0 || parsedKeypointAnnotations.length === 0) return;
    restoreAppliedRef.current = true;
    const { changed, annotations } = applyConfidenceThresholdToKeypointAnnotations(
      parsedKeypointAnnotations,
      threshold
    );
    if (changed) onKeypointAnnotationsChange(annotations);
  }, [parsedKeypointAnnotations, initialConfidenceThreshold, onKeypointAnnotationsChange, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      outsideBoundaryStateBeforePromotionRef.current.clear();
      return;
    }
    if (!hasValidBoundary) return;

    if (!showOutsideBoundaryAnnotations) {
      const demotedAnnotations = demoteOutsideBoundaryKeypoints(
        parsedKeypointAnnotations,
        outsideBoundaryStateBeforePromotionRef.current
      );
      outsideBoundaryStateBeforePromotionRef.current.clear();
      if (demotedAnnotations !== parsedKeypointAnnotations) {
        // Zero on a fresh session means no active confidence filter. Treating it
        // as an explicit slider reset would erase a saved confidence rejection.
        onKeypointAnnotationsChange(
          confidenceThreshold > 0
            ? applyConfidenceThresholdToKeypointAnnotations(demotedAnnotations, confidenceThreshold).annotations
            : demotedAnnotations
        );
      }
      return;
    }

    if (!displayAnnotations) return;

    const acceptedIds = new Set(
      parsedKeypointAnnotations
        .filter(
          (annotation) => passesConfidence(annotation) && isAnnotationEligibleForOutsideBoundaryReveal(annotation)
        )
        .map(({ id }) => id)
    );
    if (acceptedIds.size === 0) return;

    parsedKeypointAnnotations.forEach((annotation) => {
      if (acceptedIds.has(annotation.id) && !outsideBoundaryStateBeforePromotionRef.current.has(annotation.id)) {
        outsideBoundaryStateBeforePromotionRef.current.set(annotation.id, {
          visibility: annotation.keypoint.visibility,
          userVisibility: {
            ...annotation.userVisibility,
            ...(annotation.userVisibility.hiddenReasons
              ? { hiddenReasons: [...annotation.userVisibility.hiddenReasons] }
              : {}),
          },
          ...(annotation.hiddenReason !== undefined ? { hiddenReason: annotation.hiddenReason } : {}),
          ...(annotation.visibleVisibility !== undefined ? { visibleVisibility: annotation.visibleVisibility } : {}),
        });
      }
    });

    // Apply the current filter and the outside-boundary acceptance in one write.
    // This prevents the mount-time filter restore and persisted reveal setting
    // from racing and accidentally accepting a point the reviewer cannot see.
    // Keep saved confidence rejections at the default zero threshold. The slider
    // handler already removes them when a reviewer explicitly lowers it to zero.
    const filteredAnnotations =
      confidenceThreshold > 0
        ? applyConfidenceThresholdToKeypointAnnotations(parsedKeypointAnnotations, confidenceThreshold).annotations
        : parsedKeypointAnnotations;
    onKeypointAnnotationsChange(promoteOutsideBoundaryKeypoints(filteredAnnotations, acceptedIds));
  }, [
    confidenceThreshold,
    displayAnnotations,
    hasValidBoundary,
    isEditMode,
    onKeypointAnnotationsChange,
    parsedKeypointAnnotations,
    passesConfidence,
    showOutsideBoundaryAnnotations,
  ]);

  const plantsPerM2 =
    boundaryAreaSquareMeters != null && boundaryAreaSquareMeters > 0
      ? countedAnnotations.length / boundaryAreaSquareMeters
      : null;

  return (
    <div className="flex flex-1 min-h-0 w-full">
      <div className="relative flex-1 min-w-0">
        <AnnotationViewerBase
          onlyImage={!displayAnnotations}
          imageUrl={imageUrl}
          onDoubleClick={onDoubleClick}
          onImageMouseMove={handleImageMouseMove}
          onImageMouseLeave={() => setHoveredKeypointId(null)}
          onZoomChange={setTotalScale}>
          {displayBoundaryPixels.length >= 3 && (
            <Line
              points={displayBoundaryPixels.flatMap(({ x, y }) => [x, y])}
              closed
              fill="transparent"
              stroke="yellow"
              strokeWidth={10 / totalScale}
            />
          )}

          {visibleAnnotations.map(({ id, keypoint, color, userVisibility }) => {
            const display = transformPoint(keypoint.x, keypoint.y, rawWidth, rawHeight, orientation);
            const isHovered = isEditMode && displayAnnotations && id === hoveredKeypointId;
            const outsideBoundaryState = getOutsideBoundaryCandidateState({ userVisibility });
            const isRejectedOutsideBoundary = outsideBoundaryState === "rejected";
            return (
              <Fragment key={id}>
                <Circle
                  x={display.x}
                  y={display.y}
                  radius={pointLayerSize(KEYPOINT_RADIUS)}
                  fill={
                    isRejectedOutsideBoundary
                      ? "transparent"
                      : outsideBoundaryState
                        ? OUTSIDE_BOUNDARY_CANDIDATE_COLOR
                        : color
                  }
                  fillEnabled={!isRejectedOutsideBoundary}
                  stroke={outsideBoundaryState ? OUTSIDE_BOUNDARY_CANDIDATE_OUTLINE_COLOR : "white"}
                  strokeWidth={pointLayerSize(4)}
                  dash={isRejectedOutsideBoundary ? [pointLayerSize(8), pointLayerSize(6)] : undefined}
                  listening={false}
                />
                {isHovered && (
                  <Circle
                    x={display.x}
                    y={display.y}
                    // Keep the hover margin and stroke legible even with the smallest dots.
                    radius={pointLayerSize(KEYPOINT_RADIUS) + 6 / totalScale}
                    stroke="white"
                    strokeWidth={3 / totalScale}
                    fillEnabled={false}
                    listening={false}
                  />
                )}
              </Fragment>
            );
          })}
        </AnnotationViewerBase>
      </div>

      {displayAnnotations && (
        <AnnotationSidebar
          isEditMode={isEditMode}
          pointSizeSlider={{
            value: pointSizePercent,
            onChange: (percent) => {
              setPointSizePercent(percent);
              onPointSizeChange?.(percent);
            },
          }}
          stats={
            <div className="space-y-1">
              <p className="font-semibold">{countedAnnotations.length} plants</p>
              {plantsPerM2 !== null && (
                <>
                  <p>{plantsPerM2.toFixed(1)} plants/m²</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="flex items-center gap-1 cursor-help">
                          {formatCountPerAcre(countPerSquareMeterToPerAcre(plantsPerM2))} / acre
                          <Info className="h-3 w-3 opacity-70" />
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="max-w-48 text-xs">{COUNT_PER_ACRE_TOOLTIP}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          }
          instructions={
            <div className="space-y-1">
              <p>⊕ Double click → add keypoint</p>
              <p>⊖ Double click on keypoint → remove it</p>
            </div>
          }
          confidenceSlider={
            // Keep the slider visible when a restored threshold is active, even on
            // single-value images (hasConfidenceData false), so hidden keypoints can be
            // seen and reset — there's no Reset control here, so the slider is the only undo.
            isEditMode && displayAnnotations && (hasConfidenceData || confidenceThreshold > 0)
              ? { value: confidenceThreshold, onChange: handleConfidenceThresholdChange }
              : undefined
          }
          outsideBoundaryToggle={
            hasValidBoundary && onShowOutsideBoundaryAnnotationsChange
              ? {
                  value: showOutsideBoundaryAnnotations,
                  onChange: onShowOutsideBoundaryAnnotationsChange,
                }
              : undefined
          }
        />
      )}

      <OutsideBoundaryWarningDialog
        open={pendingOutsidePoint !== null}
        onOpenChange={(open) => {
          if (!open) setPendingOutsidePoint(null);
        }}
        onContinue={() => {
          setOutsideBoundarySessionChoice("keypoint", "allow");
          if (renderedPendingOutsidePoint) addKeypoint(renderedPendingOutsidePoint);
        }}
      />
    </div>
  );
};
